import { useState, SyntheticEvent, Dispatch, SetStateAction } from 'react';
import { WatchQueryOptions } from '@apollo/client';
import cn from 'classnames';
// Types
import { GetPromocode_getPromocode } from 'api/promoCodes/types/GetPromocode';
import {
  GetStream,
  GetStreamVariables,
  GetStream_stream,
} from 'api/streams/types/GetStream';
// Helpers
import { formatPrice } from 'helpers/formatPrice';
import { computeOrderStreamPath } from 'helpers/routes';
// Hooks
import { useAppContext, useGetCurrUser } from 'hooks';
// UI
// TODO update for ui3
import Icon from 'ui/Icon/Icon';
// UI2
import Button from 'ui3/Button/Button';
// Components
import ProductPayment from 'components/common3/ProductPayment/ProductPayment';
import ActivatePromoCodeModal from 'components/common3/ActivatePromoCodeModal/ActivatePromoCodeModal';
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
// Styles
import styles from './PaymentMethods.module.scss';

export type PaymentSubmitProps = {
  promoCode: GetPromocode_getPromocode | null;
  paymentMethodId: string;
  socialShareButtonText?: string;
};

type PaymentMethodsProps = {
  successPaymentModalTitle: string;
  goToLabel: string;
  successPaymentModalDescription: string | JSX.Element;
  isSuccessPaymentModalVisible: boolean;
  setSuccessPaymentModalVisibility: Dispatch<SetStateAction<boolean>>;
  paymentRedirectPath?: string;
  price: number;
  loading: boolean;
  amaId?: string;
  streamId?: string;
  confirmText?: string;
  onSubmit: (props: PaymentSubmitProps) => void;
  className?: string;
  hideConfirmIcon?: boolean;
  confirmButtonBelowPayment?: boolean;
  updateStreamQuery?: <TVars = GetStreamVariables>(
    mapFn: (
      previousQueryResult: GetStream,
      options: Pick<WatchQueryOptions<TVars, GetStream>, 'variables'>
    ) => GetStream
  ) => void;
  stream?: GetStream_stream;
  socialShareButtonText?: string;
};

const PaymentMethods = ({
  successPaymentModalTitle,
  isSuccessPaymentModalVisible,
  setSuccessPaymentModalVisibility,
  paymentRedirectPath,
  price,
  loading,
  amaId,
  streamId,
  onSubmit,
  className,
  goToLabel,
  confirmText = 'Confirm Payment',
  successPaymentModalDescription,
  updateStreamQuery,
  stream,
  socialShareButtonText,
  hideConfirmIcon = false,
  confirmButtonBelowPayment = false,
}: PaymentMethodsProps) => {
  const { setSignUpModalVisibility } = useAppContext();
  const [promoCode, setPromoCode] = useState<GetPromocode_getPromocode | null>(
    null
  );
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [
    activatePromoCodeModalOpen,
    setActivatePromoCodeModalOpen,
  ] = useState<boolean>(false);
  const { data: userData } = useGetCurrUser();

  const discount = promoCode ? price * (promoCode?.amount / 100) : 0;

  const handleCloseActivePromoCodeModal = (): void => {
    setActivatePromoCodeModalOpen(false);
  };

  const onActivatePromoCodeSuccess = (promoCode): void => {
    setPromoCode(promoCode);
    setActivatePromoCodeModalOpen(false);
  };

  const handleActivePromoCode = (
    e: SyntheticEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();

    setActivatePromoCodeModalOpen(true);
  };

  const handleRemovePromoCode = (
    e: SyntheticEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();

    setPromoCode(null);
  };

  const handlePaymentConfirm = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (userData) {
      onSubmit({
        promoCode,
        paymentMethodId,
      });
    } else {
      setSignUpModalVisibility(true);
    }
  };

  const canAddPromoCode = Boolean(amaId || streamId);

  return (
    <div
      className={cn(styles.root, className, {
        [styles.rootButtonBelowPayment]: confirmButtonBelowPayment,
      })}
    >
      <div
        className={cn(styles.actionFooter, {
          [styles.actionFooterBelowPayment]: confirmButtonBelowPayment,
        })}
      >
        {canAddPromoCode && (
          <div className={styles.actionFooterPromoCodeSection}>
            <span className={styles.price}>
              {formatPrice(price - discount)}
            </span>

            {promoCode ? (
              <div className={styles.removePromoCodeWrapper}>
                <span className={styles.orderAmaPromoCodeValue}>
                  {`${formatPrice(discount)} (Discount -${promoCode.amount}%)`}
                </span>
                <button
                  aria-label="remove promo code"
                  type="button"
                  className={styles.removePromoCodeButton}
                  onClick={handleRemovePromoCode}
                >
                  <Icon name="close-in-circle" />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.addPromoCodeButton}
                  onClick={handleActivePromoCode}
                >
                  Add Promo Code
                </button>
              </>
            )}
          </div>
        )}
        <div className={styles.actionFooterButtonSection}>
          {stream && (
            <SocialShareQRButton
              icon
              itemTitle={stream?.name || ''}
              modalTitle="Share Stream"
              modalText="Share this stream in any way convenient for you"
              path={computeOrderStreamPath(stream)}
              twitterText={socialShareButtonText || ''}
            />
          )}
          <Button
            onClick={handlePaymentConfirm}
            loading={loading}
            disabled={userData && !paymentMethodId}
            type="submit"
            className={styles.buyButton}
          >
            {!hideConfirmIcon && (
              <Icon name="buyCart" className={styles.buyCartIcon} />
            )}
            {confirmText}
          </Button>
        </div>
      </div>

      <div
        className={cn(styles.paymentCards, {
          [styles.paymentCardsFirst]: confirmButtonBelowPayment,
        })}
      >
        <ProductPayment
          onSelect={setPaymentMethodId}
          isSuccessModalVisible={isSuccessPaymentModalVisible}
          setSuccessModalVisibility={setSuccessPaymentModalVisibility}
          successModalTitle={successPaymentModalTitle}
          onSuccessRedirectPath={paymentRedirectPath}
          goToLabel={goToLabel}
          successPaymentModalDescription={successPaymentModalDescription}
          updateStreamQuery={updateStreamQuery}
        />
      </div>

      {activatePromoCodeModalOpen && (
        <ActivatePromoCodeModal
          amaId={amaId}
          streamId={streamId}
          isOpen={activatePromoCodeModalOpen}
          onClose={handleCloseActivePromoCodeModal}
          onSuccess={onActivatePromoCodeSuccess}
        />
      )}
    </div>
  );
};

export default PaymentMethods;
